import { useCallback, useRef, useState } from 'react';

// Minimal, generic async helper hook
// - run: executes a promise factory; returns the resolved value and sets loading/error accordingly
// - loading/error/value are available if you want to bind UI directly (optional)
// - abort(): cancels the latest in-flight call if it supports AbortSignal

export interface UseAsyncState<T> {
  loading: boolean;
  error: Error | null;
  value: T | null;
}

export interface UseAsync<T, Args extends unknown[]> extends UseAsyncState<T> {
  run: (...args: Args) => Promise<T>;
  abort: () => void;
}

export function useAsync<T, Args extends unknown[]>(
  factory: (signal: AbortSignal | null, ...args: Args) => Promise<T>
): UseAsync<T, Args> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [value, setValue] = useState<T | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);

  const run = useCallback(async (...args: Args) => {
    // Cancel any in-flight request
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const result = await factory(controller.signal, ...args);
      setValue(result);
      return result;
    } catch (e) {
      const err = e as Error;
      // Ignore AbortError as user-initiated cancel
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((err as any)?.name === 'AbortError') throw err;
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [factory]);

  return { run, abort, loading, error, value };
}
